import React from 'react';
import * as THREE from 'three';
import { CustomText3D } from "../../_shared/CustomText3D";
import SensorNew from "./SensorNew";
import * as global from '../../../data/global';

import { resolveLayoutCableTopPoint, normalizeCableTopPointToBinLocal, cableNumberFromCableRecord } from '../../../components/siteLayoutStice.js';
import { findLayoutBinForSiteBin } from '../../../components/layoutTableProxy.js';

export const CableNew = ({ layoutCable, layoutSensors, radiansToNorth, site, layoutBin = null }) => {
    const siteRef = site ?? global.gw.selectedSite;
    const resolvedTop = resolveLayoutCableTopPoint(
        layoutCable,
        siteRef,
        layoutBin
    );
    let topX =
        resolvedTop?.topPointX ?? layoutCable.topPointX ?? layoutCable.x;
    let topZ =
        resolvedTop?.topPointZ ?? layoutCable.topPointZ ?? layoutCable.z;

    if (!resolvedTop && siteRef?.layoutBins?.length) {
        const resolvedLayoutBin =
            layoutBin ??
            siteRef.layoutBins.find(
                (lb) =>
                    lb.customerSiteLayoutBinId ===
                    layoutCable.customerSiteLayoutBinId
            ) ??
            (layoutCable.binId != null
                ? findLayoutBinForSiteBin(siteRef, { binDataId: layoutCable.binId })
                : null);
        const norm = normalizeCableTopPointToBinLocal(
            topX,
            topZ,
            resolvedLayoutBin,
            siteRef
        );
        if (norm) {
            topX = norm.topPointX;
            topZ = norm.topPointZ;
        }
    }
    const pointVector = new THREE.Vector2(topX, topZ);
    const originVector = new THREE.Vector2(0, 0);
    const radius = pointVector.distanceTo(originVector);
    
    
    let toNorthPositionXZ = { 
        x: topX, 
        z: topZ 
    };
    if (layoutCable.aframe) {
        const sensorXs = (layoutSensors ?? [])
            .map((s) => Number(s.x))
            .filter((n) => Number.isFinite(n));
        const sensorZs = (layoutSensors ?? [])
            .map((s) => Number(s.z))
            .filter((n) => Number.isFinite(n));
        const spreadX =
            sensorXs.length > 1
                ? Math.max(...sensorXs) - Math.min(...sensorXs)
                : 0;
        const spreadZ =
            sensorZs.length > 1
                ? Math.max(...sensorZs) - Math.min(...sensorZs)
                : 0;

        if (layoutSensors?.length > 0 && (spreadX > 0.5 || spreadZ > 0.5)) {
            const points = layoutSensors.map((layoutSensor) => ({
                x: layoutSensor.x,
                y: layoutSensor.z,
            }));
            const center = global.getCenterOfPoints(points);
            toNorthPositionXZ.x = center.x;
            toNorthPositionXZ.z = center.y;
        } else {
            const lx = layoutCable.x ?? layoutCable.topPointX ?? topX;
            const lz = layoutCable.z ?? layoutCable.topPointZ ?? topZ;
            toNorthPositionXZ.x = lx;
            toNorthPositionXZ.z = lz;
        }
        if (layoutCable.aframe?.height != null) {
            layoutCable.topPointY = layoutCable.aframe.height - 3;
        }
    } else if (Number.isFinite(radiansToNorth) && Math.abs(radiansToNorth) > 0.001) {
        toNorthPositionXZ = global.shiftVectorByRadiansOnCircleXZ({ 
            x: topX, 
            y: topZ 
        }, radiansToNorth, radius);
    }


    //const globalPositionScale = grainWave.sites[siteIndex].globalScale.globalPositionScale;
    //const globalOffset = 2.5;

    //const points = [];
    //points.push(new THREE.Vector3(layoutCable.bottomPointX, layoutCable.bottomPointY, layoutCable.bottomPointZ));
    //points.push(new THREE.Vector3(layoutCable.topPointX, layoutCable.topPointY, layoutCable.topPointZ));
    //const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);

    //////  Test cable length vs bin diameter
    ////points.push(new THREE.Vector3(layoutCable.height / 2.0, 0, 0));
    ////points.push(new THREE.Vector3(-layoutCable.height / 2.0, 0, 0));

    // 3D bins and flats already show which bin you are in. Labels are the cable number only
    // (B-12 / Big House-12 -> 12). Fall back to the full name when there is no trailing number.
    const cableNum = cableNumberFromCableRecord({
        name: layoutCable?.cableName,
        cableName: layoutCable?.cableName,
    });
    let cableName = "";
    if (cableNum != null) {
        cableName = String(cableNum);
    } else if (layoutCable?.cableName) {
        cableName = layoutCable.cableName;
    } else if (layoutCable?.cableId != null) {
        cableName = String(layoutCable.cableId);
    }

    // Look up cable grade - try binId+cableId (layoutCable uses binDataId as binId)
    const cableGradeKey = layoutCable?.binId != null && layoutCable?.cableId != null
        ? `${layoutCable.binId}_${layoutCable.cableId}` : null;
    const cableGradeData = cableGradeKey && global.gw.selectedSite?.cableGrades?.has(cableGradeKey)
        ? global.gw.selectedSite.cableGrades.get(cableGradeKey) : null;
    const cableGrade = cableGradeData?.grade ?? null;
    const averageTimeToRead = cableGradeData?.averageTimeToRead ?? null;

    return (
        <group
            position={[0, 0, 0]}
            //rotation={[0, 0, 0]}
            //position ={layoutCable.aframe ? [layoutCable.aframe.centerX, layoutCable.aframe.centerY, layoutCable.aframe.centerZ] : [0, 0, 0]}
            //position ={[aframeCablePosition.x, aframeCablePosition.y, -aframeCablePosition.z]}
            //rotation={layoutCable.aframe ? [Math.PI / 2, 0, 0] : [0, 0, 0]}
            ref={el => {
                if (!el) return;

                if (!global.gw.selectedSite.cablesRef.includes(el)) {
                    el.name = layoutCable.name;
                    el.groupId = layoutCable.customerSiteLayoutGroupId;
                    el.binId = layoutCable.customerSiteLayoutBinId;
                    el.cableId = layoutCable.cableId; //layoutCable.customerSiteLayoutCableId;
                    el.meshes = [];
                    el.children.forEach(g => {
                        if (g.isLevel) {
                            global.gw.selectedSite.cableLevelRef.push(g);
                        }
                        else {
                            g.children.forEach(gg => {
                                if (gg.meshProps && gg.meshProps.sensorId > 0) {
                                    global.gw.selectedSite.sensorsGroupRef.push(gg);
                                }
                                gg.children.forEach(ggg => {
                                    if (ggg.isMesh) {
                                        if (ggg.name === "AnSpace") ggg.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                                        el.meshes.push(ggg);
                                    }
                                    if (ggg.isGroup) {
                                        ggg.children.forEach(m => {
                                            if (m.name === "AnSpace") m.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                                            el.meshes.push(m);
                                            global.gw.selectedSite.sensorsRef.push(m);
                                        });
                                    }
                                })
                            })
                        }
                    });
                    global.gw.selectedSite.cablesRef.push(el);
                    //if (layoutCable.cableName) {

                    //}
                }
            }}
        >
            <CustomText3D
                isCableName={true}
                cableId={layoutCable.cableId}
                layoutCableId={layoutCable.customerSiteLayoutCableId}
                scale={(global.gw.selectedSite?.binTextScale ?? 0.35) * 0.15}
                side={THREE.DoubleSide}
                position={[toNorthPositionXZ.x, layoutCable.topPointY + 1, toNorthPositionXZ.z]}
                meshProps={{
                    material: new THREE.MeshStandardMaterial({
                        color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                        side: THREE.FrontSide,
                        toneMapped: false,
                        envMapIntensity: 1,
                        metalness: 0.0,
                        transparent: false,
                        opacity: 1,
                    }),
                }}
            >
                {cableName}
            </CustomText3D>
            {cableGrade != null && global.gw.selectedSite?.isGradesOn !== false && global.gw.selectedSite?.isCableFocused && (global.gw.selectedSite?.focusedCableId == null || global.gw.selectedSite.focusedCableId == layoutCable.cableId) && (
                <CustomText3D
                    scale={(global.gw.selectedSite?.binTextScale ?? 0.35) * 0.15}
                    side={THREE.DoubleSide}
                    position={[toNorthPositionXZ.x, layoutCable.topPointY - 0.72, toNorthPositionXZ.z]}
                    meshProps={{
                        material: new THREE.MeshStandardMaterial({
                            color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                            side: THREE.FrontSide,
                            toneMapped: false,
                            envMapIntensity: 1,
                            metalness: 0.0,
                            transparent: false,
                            opacity: 1,
                        }),
                    }}
                >
                    {`GQCS ${Math.round(cableGrade)}%`}{averageTimeToRead != null && !isNaN(averageTimeToRead) ? ` ${averageTimeToRead}s` : ''}
                </CustomText3D>
            )}
            <mesh
                scale={1.0}
                //position={[layoutCable.topPointX, layoutCable.topPointY + 1, layoutCable.topPointZ]}
                position={[toNorthPositionXZ.x, layoutCable.topPointY + 1, toNorthPositionXZ.z]}
                cableId={layoutCable.cableId}
                isLevel={true}
            >
                <boxGeometry args={[0.5, 0.5, 0.5]} />
                <meshStandardMaterial
                    color={"rgb(235, 211, 124)"}
                    emissive={"rgb(235, 211, 124)"}
                    toneMapped={false}
                    envMapIntensity={1}
                    metalness={0.0}
                    transparent={true}
                    visible={false}
                    opacity={0}
                />
            </mesh>
            {/*<CustomText3D*/}
            {/*    color={"#557755"}*/}
            {/*    scale={0.05}*/}
            {/*    side={THREE.DoubleSide}*/}
            {/*    position={[layoutCable.bottomPointX, layoutCable.bottomPointY, layoutCable.bottomPointZ]}*/}
            {/*>*/}
            {/*    {!layoutCable.cableName ? layoutCable.cableId : layoutCable.cableName.split('-')[1]}*/}
            {/*</CustomText3D>*/}

            {layoutSensors.map((layoutSensor) => (
                <React.Fragment key={`sensor-container-${layoutSensor.customerSiteLayoutSensorId}`}>
                    {(() => {
                        let aframe = layoutCable.aframe;
                        if (aframe) {
                            let aframePosition = { x: aframe.startX, y: aframe.startY, z: aframe.startZ };
                        }
                        return (
                            <></>
                        )
                    })()}
                    <SensorNew
                        layoutSensor={layoutSensor}
                        position={layoutCable.aframe ? { x: layoutSensor.x, y: layoutSensor.y, z: layoutSensor.z } : { x: toNorthPositionXZ.x, y: layoutSensor.y, z: toNorthPositionXZ.z }}
                    />
                </React.Fragment>
            ))}

            {/*<line*/}
            {/*    geometry={lineGeometry}>*/}
            {/*    <lineBasicMaterial*/}
            {/*        attach="material"*/}
            {/*        color={"white"}*/}
            {/*        linewidth={1}*/}
            {/*        linecap={"round"}*/}
            {/*        linejoin={"round"}*/}
            {/*        transparent*/}
            {/*        opacity={1}*/}
            {/*    />*/}
            {/*</line>*/}
        </group>
    );
}

export default CableNew;

//position = { new THREE.Vector3(cable.topPointX / globalPositionScale, topPoint.y / globalPositionScale - topPoint.y * 0.02, cable.topPointZ / globalPositionScale) }
//{/*<rectAreaLight*/ }
//{/*    intensity={2}*/ }
//{/*    color={[255, 255, 255]}*/ }
//{/*    lookAt={new THREE.Vector3(cable.topPointX / globalPositionScale, topPoint.y / globalPositionScale - topPoint.y * 0.03, cable.topPointZ / globalPositionScale)}*/ }
//{/*    position={new THREE.Vector3(cable.topPointX / globalPositionScale, topPoint.y / globalPositionScale - topPoint.y * 0.03, cable.topPointZ / globalPositionScale)}*/ }
//{/*    width={0.05}*/ }
//{/*    height={0.05}*/ }
//{/*/>*/ }

//color={'#9c88ff'}
